import React, { useState, useRef, useEffect } from 'react';
import { Slider } from '../slider/slider.js';
import { Button } from '../button/button.js';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from '@nackle/origami-icons';

import './nackleScroll.less';

import classNames from 'classnames';

/**
 * A custom scrollbar
 *
 * @module NackleScroll
 * @param { Object } props - the component props
 * @param { string } [props.className] - the component className(s)
 * @param { number } [props.step] - the value added/subtracted when the buttons are pressed
 * @param { Function } [props.onChange ] - the function that is called onchange.
 * @param { boolean } [ props.vertical ] - whether or not the scroll is vertical
 * @param { number } [props.value] - the slider value controlled from outside
 * @returns { React.ReactElement } A NackleScroll component
**/
const NackleScroll = ( props ) => {
    const {
        className,
        step = 1,
        onChange: onScrollChange,
        vertical,
        value
    } = props;

    const intervalRef = useRef( null );

    const [ internalValue, setValue ] = useState( 0 );

    const scrollClasses = classNames( {
        [ className ]: className ? true : false,
        'scroll-slider': true
    } );

    useEffect( () => {
        return () => {
            if ( intervalRef.current ) {
                clearInterval( intervalRef.current );
            }
        };
    }, [] );

    useEffect( () => {
        if ( value ) {
            setValue( value );
        }
    }, [ value ] );

    const startHold = ( changeValue ) => {
        // clear existing interval
        if ( intervalRef.current ) {
            clearInterval( intervalRef.current );
        }

        //Start new interval, runs every 100ms
        intervalRef.current = setInterval( () => onButtonPress( changeValue ), 100 );
    };

    const stopHold = () => {
        //clear interfval on release
        if ( intervalRef.current ) {
            clearInterval( intervalRef.current );
            intervalRef.current = null;
        }
    };

    const onButtonPress = ( changeValue ) => {
        const checkValue = value !== undefined ? value : internalValue;
        if ( checkValue <= 100 && checkValue >= 0 ) {
            setValue( prevValue => {
                if ( prevValue + changeValue <= 100 && ( prevValue + changeValue >= 0 ) ) {
                    if ( typeof onScrollChange === 'function' ) {
                        onScrollChange( prevValue + changeValue );
                    }
                    return prevValue + changeValue;
                } else {
                    return prevValue;
                }
            } );
        }
    };

    const onChange = ( changeValue ) => {
        setValue( changeValue );
        if ( typeof onScrollChange === 'function' ) {
            onScrollChange( changeValue );
        }
    };

    return (
        <div className={ vertical ? 'scrollbar-container vertical-scrollbar-container' : 'scrollbar-container' }>
            <Button 
                className="scroll-button"
                onClick={ () => onButtonPress( -1 * step ) }
                icon={ vertical ? <ChevronUp /> : <ChevronLeft /> }
                size="small"
                type="text"
                onMouseDown={ () => startHold( -1 * step ) }
                onMouseUp={ () => stopHold() }
                onMouseLeave={ () => stopHold() }
            />
            <Slider
                { ...props }
                showMarks={ false }
                className={ scrollClasses }
                value={ value !== undefined ? value : internalValue }
                onChange={ onChange }
                reverse={ vertical ? true : false }
            />
            <Button 
                className="scroll-button"
                onClick={ () => onButtonPress( step ) }
                icon={ vertical ? <ChevronDown /> : <ChevronRight /> }
                size="small"
                type="text"
                onMouseDown={ () => startHold( step ) }
                onMouseUp={ () => stopHold() }
                onMouseLeave={ () => stopHold() }
            />
        </div>
    );
};
export { NackleScroll };
